import SmartDisplayOutlined from '@mui/icons-material/SmartDisplayOutlined';
import SubtitlesOutlined from '@mui/icons-material/SubtitlesOutlined';
import { difference } from '@openx/components-form/difference';
import type { BidderCreativeCreateParams, BidderCreativeUpdateParams } from '@openx/types/gqlTypes';
import { AdFormat, BidderCreativeStatus, MarkupType } from '@openx/types/gqlTypes';
import { exhaustiveCheck } from '@openx/utils/lib/exhaustiveCheck';
import type { TFunction } from 'i18next';
import type { ReactElement } from 'react';
import type { GetBidderCreativeQuery } from '../../../graphql/query/creatives/GetBidderCreative.generated';
import type { AdvertiserOption } from '../types';
import { getAdFormatLabels, getAdvertiserOptionsMap, getStatusLabels } from '../utils';
import type { CreativeFormState } from './validation/validationSchema';
import type { VideoCreativeFormData } from './validation/videoCreative';

/**
 * Returns initial form state for **creating** a new Creative
 * Always initializes with Video format and valid default values
 * When only one advertiser is available, it is automatically pre-selected
 */
export const getCreativeCreateInitialState = (advertiserOptions: AdvertiserOption[] = []): VideoCreativeFormData => {
  const optionKeys = Object.keys(getAdvertiserOptionsMap(advertiserOptions));

  return {
    account_uid: optionKeys.length === 1 ? optionKeys[0] : '',
    name: '',
    status: BidderCreativeStatus.Active,
    ad_format: AdFormat.Video,
    markup_payload: '',
    crid: '',
    iab_categories: [],
    advertiser_domain: '',
  };
};

/**
 * Transforms creative API data to form values for **editing**
 * Respects the discriminated union types (Banner/Video/Audio) used in the form
 * Maps API fields to form fields with proper type conversions
 */
export const getCreativeEditInitialState = (
  creative: NonNullable<GetBidderCreativeQuery['bidderCreativeById']>
): CreativeFormState => {
  const { ad_format } = creative;

  switch (ad_format) {
    case AdFormat.Banner:
      return {
        ad_format: AdFormat.Banner,
        account_uid: creative.account_uid,
        name: creative.name,
        status: creative.status,
        markup_payload: creative.markup_payload,
        crid: creative.crid,
        width: creative.width?.toString() ?? '',
        height: creative.height?.toString() ?? '',
        iab_categories: creative.iab_categories ?? [],
        advertiser_domain: creative.advertiser_domain ?? '',
      };

    case AdFormat.Video:
      return {
        ad_format: AdFormat.Video,
        account_uid: creative.account_uid,
        name: creative.name,
        status: creative.status,
        markup_payload: creative.markup_payload,
        crid: creative.crid,
        iab_categories: creative.iab_categories ?? [],
        advertiser_domain: creative.advertiser_domain ?? '',
      };

    default:
      return exhaustiveCheck(ad_format);
  }
};

/**
 * Normalize form values based on ad format
 * Adds dimensions for banner format, removes them for other formats
 */
export const normalizeFormValuesByFormat = (values: CreativeFormState, newFormat: AdFormat): CreativeFormState => {
  if (newFormat === AdFormat.Banner) {
    return { ...values, width: '', height: '', ad_format: newFormat };
  }

  if ('width' in values && 'height' in values) {
    const { width, height, ...rest } = values;

    return { ...rest, ad_format: newFormat };
  }

  return { ...values, ad_format: newFormat };
};

/**
 * Get status options for radio field
 */
export const getStatusOptions = (t: TFunction): Array<{ value: BidderCreativeStatus; label: string }> => {
  const statusLabels = getStatusLabels(t);

  return [
    {
      value: BidderCreativeStatus.Active,
      label: statusLabels[BidderCreativeStatus.Active],
    },
    {
      value: BidderCreativeStatus.Paused,
      label: statusLabels[BidderCreativeStatus.Paused],
    },
  ];
};

/**
 * Get format options for radio field
 */
export const getFormatOptions = (
  t: TFunction
): Array<{
  value: AdFormat;
  dataTest: string;
  helperText: string;
  label: string;
  isAllowed: boolean;
  child: null;
  icon: ReactElement;
}> => {
  const adFormatLabels = getAdFormatLabels(t);

  return [
    {
      value: AdFormat.Video,
      dataTest: 'video-format-radio',
      helperText: '',
      label: adFormatLabels[AdFormat.Video],
      isAllowed: true,
      child: null,
      icon: <SmartDisplayOutlined sx={{ color: 'text.secondary' }} />,
    },
    {
      value: AdFormat.Banner,
      dataTest: 'banner-format-radio',
      helperText: '',
      label: adFormatLabels[AdFormat.Banner],
      isAllowed: true,
      child: null,
      icon: <SubtitlesOutlined sx={{ color: 'text.secondary' }} />,
    },
  ];
};

/**
 * Maps the ad format to the appropriate default markup type
 */
const getDefaultMarkupType = (adFormat: AdFormat): MarkupType => {
  switch (adFormat) {
    case AdFormat.Banner:
      return MarkupType.Html;
    case AdFormat.Video:
      return MarkupType.Vast;
    default:
      return exhaustiveCheck(adFormat);
  }
};

/**
 * Transforms form data to the format expected by the createBidderCreative mutation
 */
export const transformFormDataToMutationInput = (formData: CreativeFormState): BidderCreativeCreateParams => {
  const input: BidderCreativeCreateParams = {
    account_uid: formData.account_uid,
    name: formData.name,
    crid: formData.crid,
    ad_format: formData.ad_format,
    markup_type: getDefaultMarkupType(formData.ad_format),
    markup_payload: formData.markup_payload,
    status: formData.status,
    iab_categories: formData.iab_categories,
    advertiser_domain: formData.advertiser_domain.trim(),
  };

  if (formData.ad_format === AdFormat.Banner) {
    input.width = parseInt(formData.width, 10);
    input.height = parseInt(formData.height, 10);
  }

  return input;
};

const transformFormDataToUpdateComparisonInput = (formData: CreativeFormState): BidderCreativeUpdateParams => {
  const { account_uid: _accountUid, crid: _crid, ...input } = transformFormDataToMutationInput(formData);

  return input;
};

export const getChangedCreativeUpdateInput = (
  currentValues: CreativeFormState,
  initialValues: CreativeFormState
): BidderCreativeUpdateParams =>
  difference(
    transformFormDataToUpdateComparisonInput(currentValues),
    transformFormDataToUpdateComparisonInput(initialValues)
  );
